import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import usePlayerManifest from "../hooks/usePlayerManifest";
import PasswordInput from "../components/PasswordInput";
import storage from '../nativeStorage';

export default function PlayerVerify() {
  usePlayerManifest();
  const { player_id, event_id } = useParams();
  const [scorecard, setScorecard] = useState(null);
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const token = storage.getItem("player_token");

  useEffect(() => {
    if (!token) { navigate("/player/login"); return; }
    loadScorecard();
  }, []);

  const loadScorecard = async () => {
    try {
      const res = await fetch("/api/player/scorecard/" + player_id + "/" + event_id, {
        headers: { "Authorization": "Bearer " + token }
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || "Failed to load scorecard");
      setScorecard(data);
    } catch(e) { setError("Network error"); }
    finally { setLoading(false); }
  };

  const verifyScores = async () => {
    if (!/^[0-9]{4}$/.test(pin)) return setError("Please enter your 4-digit PIN");
    setVerifying(true); setError("");
    try {
      const res = await fetch("/api/player/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify({ player_id: parseInt(player_id), event_id: parseInt(event_id), pin })
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || "Verification failed");
      setSuccess(true);
    } catch(e) { setError("Network error"); }
    finally { setVerifying(false); }
  };

  if (loading) return <div style={{ minHeight: "100vh", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>Loading scorecard...</div>;

  return (
    <div style={{ minHeight: "100vh", background: "#f0fdf4" }}>
      <div style={{ background: "#14532d", color: "white", padding: "14px 20px", display: "flex", alignItems: "center", gap: "10px" }}>
        <span style={{ fontSize: "24px" }}>⛳</span>
        <div>
          <div style={{ fontWeight: "800", fontSize: "15px" }}>Score Verification</div>
          <div style={{ fontSize: "11px", color: "#86efac" }}>Marker Confirmation</div>
        </div>
        <button onClick={() => navigate("/player/dashboard")} style={{ marginLeft: "auto", background: "rgba(255,255,255,0.2)", color: "white", border: "none", borderRadius: "6px", padding: "6px 14px", fontSize: "12px", cursor: "pointer" }}>← Back</button>
      </div>

      <div style={{ padding: "20px", maxWidth: "500px", margin: "0 auto" }}>

        {success ? (
          <div style={{ background: "white", borderRadius: "10px", padding: "32px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: "48px", marginBottom: "12px" }}>✅</div>
            <h2 style={{ fontSize: "18px", fontWeight: "800", color: "#14532d", marginBottom: "8px" }}>Scores Verified!</h2>
            <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "20px" }}>The scores for {scorecard?.player?.name} have been verified and locked. They cannot be changed.</p>
            <button onClick={() => navigate("/player/dashboard")} style={{ background: "#14532d", color: "white", border: "none", borderRadius: "8px", padding: "10px 24px", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}>Done</button>
          </div>
        ) : (
          <>
            {scorecard?.verified && (
              <div style={{ background: "#dcfce7", border: "1px solid #16a34a", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px", fontSize: "13px", color: "#14532d", fontWeight: "600" }}>
                ✅ These scores have already been verified
              </div>
            )}

            <div style={{ background: "white", borderRadius: "10px", padding: "16px", marginBottom: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <h2 style={{ fontSize: "14px", fontWeight: "700", color: "#14532d", marginBottom: "12px" }}>
                Scorecard — {scorecard?.player?.name}
              </h2>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ background: "#14532d", color: "white" }}>
                    <th style={{ padding: "6px 10px", textAlign: "center" }}>Hole</th>
                    <th style={{ padding: "6px 10px", textAlign: "center" }}>Gross Score</th>
                  </tr>
                </thead>
                <tbody>
                  {scorecard?.scores?.map((s, i) => (
                    <tr key={s.hole} style={{ background: i % 2 === 0 ? "white" : "#f0fdf4", borderBottom: "1px solid #e5e7eb" }}>
                      <td style={{ padding: "7px 10px", textAlign: "center", fontWeight: "600" }}>{s.hole}</td>
                      <td style={{ padding: "7px 10px", textAlign: "center", fontWeight: "700", color: "#14532d" }}>{s.gross}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: "10px", padding: "8px 12px", background: "#f0fdf4", borderRadius: "6px", fontSize: "12px", color: "#374151" }}>
                Total holes: {scorecard?.scores?.length || 0} / 18
              </div>
            </div>

            {!scorecard?.verified && (
              <div style={{ background: "white", borderRadius: "10px", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                <h2 style={{ fontSize: "14px", fontWeight: "700", color: "#14532d", marginBottom: "12px" }}>Confirm These Scores</h2>
                <p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "14px" }}>By entering your PIN you confirm these scores are correct. Scores will be permanently locked.</p>

                {error && <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "8px 12px", borderRadius: "6px", fontSize: "13px", marginBottom: "12px" }}>{error}</div>}

                <PasswordInput maxLength="4" value={pin} onChange={e => setPin(e.target.value)} placeholder="Enter your 4-digit PIN"
                  style={{ width: "100%", border: "2px solid #d1d5db", borderRadius: "8px", padding: "12px", fontSize: "20px", textAlign: "center", letterSpacing: "12px" }}
                  wrapperStyle={{ marginBottom: "12px" }} />

                <button onClick={verifyScores} disabled={verifying || scorecard?.scores?.length < 18}
                  style={{ width: "100%", background: "#7c3aed", color: "white", border: "none", borderRadius: "8px", padding: "12px", fontSize: "14px", fontWeight: "700", cursor: "pointer", opacity: verifying ? 0.7 : 1 }}>
                  {verifying ? "Verifying..." : "✅ Confirm & Lock Scores"}
                </button>

                {scorecard?.scores?.length < 18 && (
                  <p style={{ fontSize: "11px", color: "#f97316", textAlign: "center", marginTop: "8px" }}>All 18 holes must be entered before verification</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
